import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiMenu, FiX, FiShoppingCart } from 'react-icons/fi';
import { useCart } from '../context/CartContext';

const HeaderShed = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const location = useLocation();
  const { cartCount } = useCart();

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsScrolled(currentScrollY > 50);
      
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Sheds', href: '/sheds' },
    { name: 'Dealers', href: '/dealers' },
    { name: 'Pricing', href: '/pricing' },
    { name: 'Shed Wanted', href: '/shed-wanted' },
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '/contact' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Main Header */}
      <header
        className={`fixed top-0 left-0 right-0 w-full transition-all duration-300 ${
          isScrolled ? 'border-b border-gray-200 bg-white' : 'bg-white'
        } ${isVisible ? 'translate-y-0' : 'lg:-translate-y-full'}`}
        style={{ zIndex: 9996 }}
      >
        <div className="container mx-auto px-4">
          {/* Desktop Header */}
          <div className="hidden lg:flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <span className="text-xl font-semibold tracking-tight text-gray-900">Shed</span>
              <span className="text-xl font-semibold tracking-tight text-gray-900">X</span>
            </Link>

            {/* Navigation */}
            <nav className="flex items-center gap-8">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`text-sm transition-colors ${
                    isActive(item.href)
                      ? 'text-gray-900 font-medium'
                      : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-5">
              <Link
                to="/dealer/login"
                className="text-xs text-gray-400 hover:text-gray-700 transition-colors"
              >
                Dealer Login
              </Link>
              <Link
                to="/cart"
                className="relative text-gray-500 hover:text-gray-900 transition-colors"
              >
                <FiShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 w-4 h-4 bg-gray-900 text-white text-[10px] font-medium rounded-full flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Link>
              <Link
                to="/sheds"
                className="bg-gray-900 text-white px-5 py-2 text-sm font-medium hover:bg-gray-800 transition-colors"
              >
                Browse Sheds
              </Link>
            </div>
          </div>

          {/* Mobile Header */}
          <div className="flex lg:hidden justify-between items-center h-14">
            <Link to="/" className="text-lg font-semibold tracking-tight text-gray-900">
              ShedX
            </Link>
            <div className="flex items-center gap-3">
              <Link
                to="/cart"
                className="relative text-gray-500"
              >
                <FiShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 w-4 h-4 bg-gray-900 text-white text-[10px] font-medium rounded-full flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Link>
              <button
                className="p-1 text-gray-500"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Toggle menu"
              >
                {isMenuOpen ? (
                  <FiX className="w-5 h-5" />
                ) : (
                  <FiMenu className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div 
          className={`fixed inset-0 bg-black/50 transition-opacity duration-200 lg:hidden ${
            isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
          style={{ zIndex: 9998 }}
          onClick={() => setIsMenuOpen(false)}
        />
        
        {isMenuOpen && (
          <aside 
            className="fixed top-0 right-0 bg-white lg:hidden flex flex-col"
            style={{ 
              height: '100vh',
              width: '80%',
              maxWidth: '320px',
              zIndex: 9999
            }}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <span className="text-sm font-medium text-gray-900 uppercase tracking-wider">Menu</span>
              <button
                onClick={() => setIsMenuOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto py-6 px-6">
              <nav className="space-y-1">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`block py-3 border-b border-gray-100 text-sm transition-colors ${
                      isActive(item.href)
                        ? 'text-gray-900 font-medium'
                        : 'text-gray-500 hover:text-gray-900'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
              </nav>

              <div className="mt-8 space-y-3">
                <Link
                  to="/sheds"
                  className="block bg-gray-900 text-white px-5 py-3 text-sm font-medium text-center hover:bg-gray-800 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Browse Sheds
                </Link>
                <Link
                  to="/dealer/login"
                  className="block text-center text-xs text-gray-400 hover:text-gray-600 transition-colors py-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Dealer Login
                </Link>
              </div>
            </div>
          </aside>
        )}
      </header>
    </>
  );
};

export default HeaderShed;
